import { Briefcase, Clock, Hash } from "lucide-react";
import { useTranslation } from "react-i18next";

import EmptyState from "@/components/ui/v2/empty-state";
import { RelativeTime } from "@/components/ui/v2/relative-time";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import type { SegmentVacancyPreviewResponse } from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";

type IncludedVacanciesSectionProps = {
  vacancies: SegmentVacancyPreviewResponse;
};

export function IncludedVacanciesSection({
  vacancies,
}: IncludedVacanciesSectionProps) {
  const { t } = useTranslation();
  const isEmpty = vacancies.totalCount === 0;
  const targetRef = useTourTarget<HTMLElement>(SEGMENT_TOUR_TARGET.OVERVIEW_VACANCIES);

  if (isEmpty) {
    return (
      <section ref={targetRef}>
        <h3 className="mb-3 text-sm font-semibold">
          {t("admin.segments.includedVacancies", "Included vacancies")}
        </h3>
        <EmptyState
          layout="inline"
          icon={Briefcase}
          title={t("admin.segments.noVacanciesMatched", "No vacancies matched")}
          description={t(
            "admin.segments.noVacanciesMatchedDescription",
            "Vacancies matching this segment's rules will appear here.",
          )}
        />
      </section>
    );
  }

  const remaining = vacancies.totalCount - vacancies.preview.length;

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {t("admin.segments.includedVacancies", "Included vacancies")}
        </h3>
        <span className="text-xs text-muted-foreground">
          {vacancies.totalCount} {t("common.total", "total")}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {vacancies.preview.map((vacancy, index) => (
          <div
            key={vacancy.vacancyId}
            ref={index === 0 ? targetRef : undefined}
            data-slot="panel"
            className="rounded-lg border border-border bg-card p-3"
          >
            <p className="truncate text-sm font-medium">{vacancy.title}</p>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              {vacancy.externalId && (
                <span className="flex items-center gap-1">
                  <Hash className="size-3" />
                  {vacancy.externalId}
                </span>
              )}
              {vacancy.addedAt && (
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  {t("common.added", "Added")}{" "}
                  <RelativeTime date={vacancy.addedAt} />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {remaining}{" "}
          {t(
            "admin.segments.moreVacancies",
            "more vacancies. Open the builder to see all",
          )}
        </p>
      )}
    </section>
  );
}
